import * as React from 'react';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';
import { colors } from 'kaizen-ui-colors/lib';
import Organization from '../../../domain/entity/Organization';
import { IStore } from '../../../domain/store/IStore';
import { IUsecase } from '../../../application/usecase/IUsecase';
import { Divider } from '../Divider';
import ArrowDown from '../base/Icon/Kaizen/ArrowDown';
import Dropdown, { IDropdownItem } from '../Dropdown';
import OrganizationListItem from '../OrganizationListItem';
import { elevation2 } from '../../sharedStyle/elevation';

interface IProps {
  store?: IStore;
  usecase?: IUsecase;
  toggleMode: (e: React.SyntheticEvent<HTMLElement>) => void;
}

const OrganizationHorizontalList = styled.div`
  display: flex;
  overflow-x: scroll;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const OrganizationHorizontalListItem = styled.div`
  height: 56px;
  line-height: 56px;
  margin-right: 4px;
  margin-left: 4px;
  padding-right: 8px;
  padding-left: 8px;
  font-size: 13px;
  font-weight: bold;
  color: ${colors.ash_dark__878fa0};
  white-space: nowrap;
  cursor: pointer;

  &[aria-selected='true'] {
    color: ${colors.slate__4c566c};
    border-bottom: 4px solid ${colors.veggie__2dcc70};
    color: ${colors.veggie__2dcc70};
    cursor: initial;

    &:hover {
      color: ${colors.veggie__2dcc70};
    }
  }

  &:hover {
    color: ${colors.slate__4c566c};
  }
`;

const Menu = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
`;

const ToggleModeButton = styled.button`
  text-decoration: none;
  font-size: 1rem;
  line-height: 2.25rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  display: inline-flex;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  height: 36px;
  padding: 0 16px;
  border: none;
  outline: none;
  text-align: center;
  user-select: none;
  -webkit-appearance: none;
  overflow: hidden;
  vertical-align: middle;
  border-radius: 2px;
  background-color: ${colors.metal__8293b9};
  ${elevation2};
  color: white;
  white-space: nowrap;

  &:hover {
    cursor: pointer;
  }
`;

const AdditionalComponents = styled.div`
  display: none;

  @media (min-width: 576px) {
    display: flex;
    align-items: center;
  }
`;

@inject('store', 'usecase')
@observer
export class RoundMonitorToolbar extends React.Component<IProps, {}> {
  private store: IStore;
  private usecase: IUsecase;

  constructor(props: IProps) {
    super(props);
    this.store = props.store!;
    this.usecase = props.usecase!;
    this.getOrganizationProject = this.getOrganizationProject.bind(this);
    this.onChangeOrganization = this.onChangeOrganization.bind(this);
    this.onClickOrganization = this.onClickOrganization.bind(this);
  }

  private async getOrganizationProject(organizationId: string) {
    const { projectUsecase } = this.usecase;
    await projectUsecase.getProjects(organizationId);
  }

  public onChangeOrganization(e: React.MouseEvent<HTMLElement>, selectedKey: string) {
    const { organizationStore } = this.store;

    e.stopPropagation();
    organizationStore.setSelectedOrganizationId(selectedKey);
    this.getOrganizationProject(selectedKey);
  }

  private onClickOrganization(e: React.MouseEvent<HTMLElement>) {
    const { organizationStore } = this.store;
    const organizationId = e.currentTarget.getAttribute('data-id');
    if (!organizationId) {
      return;
    }
    organizationStore.setSelectedOrganizationId(organizationId);
    this.getOrganizationProject(organizationId);
    window.scrollTo(0, 0);
  }

  private renderOrganizationHorizontalList(organizations: Organization[], selectedOrganizationId: string) {
    return (
      <OrganizationHorizontalList>
        {organizations.map(organization => {
          const name =
            organization.username.length < 20 ? organization.username : `${organization.username.substr(0, 19)}...`;
          return (
            <OrganizationHorizontalListItem
              key={organization.id}
              title={organization.username}
              aria-selected={organization.id === selectedOrganizationId}
              data-id={organization.id}
              onClick={this.onClickOrganization}
            >
              {name}
            </OrganizationHorizontalListItem>
          );
        })}
      </OrganizationHorizontalList>
    );
  }

  private renderOrganizationVertialList(organizations: Organization[], selectedOrganizationId: string) {
    const TriggerComponent = (
      <Menu>
        <span>組織を選択</span>
        <ArrowDown size={30} />
      </Menu>
    );
    const ItemComponents: IDropdownItem[] = [];
    organizations.map(organization => {
      ItemComponents.push({
        key: organization.id,
        component: (
          <OrganizationListItem
            name={organization.username}
            profileImageUrl={organization.profileImageUrl}
            currentOrganization={organization.id === selectedOrganizationId}
          />
        ),
      });
    });
    return (
      <Dropdown
        placement="bottom-end"
        triggerComponent={TriggerComponent}
        items={ItemComponents}
        onChange={this.onChangeOrganization}
      />
    );
  }

  public render() {
    const { exclusionListStore, organizationStore } = this.store;
    const organizations = organizationStore.organizations.filter(
      organization => exclusionListStore.exclusionLists.indexOf(Number(organization.id)) === -1,
    );

    return (
      <React.Fragment>
        {this.renderOrganizationHorizontalList(organizations, organizationStore.selectedOrganizationId)}
        <AdditionalComponents>
          <Divider />
          {this.renderOrganizationVertialList(organizations, organizationStore.selectedOrganizationId)}
          <Divider />
          <div>
            <ToggleModeButton onClick={this.props.toggleMode}>メニュー設定</ToggleModeButton>
          </div>
        </AdditionalComponents>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/presentation/component/Toolbar/RoundMonitorToolbar.tsx